import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWrench, faPlus, faXmark, faCheck, faSpinner,
  faTriangleExclamation, faCircleCheck, faArrowsRotate,
  faLocationDot, faUser, faNoteSticky, faTrash,
} from "@fortawesome/free-solid-svg-icons";
import DashboardLayout from "../layout/DashboardLayout";
import "./MaintenancePage.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

const PRIORITY_META: Record<string, { label: string; color: string; bg: string }> = {
  low:    { label: "Low",    color: "#34d399", bg: "rgba(52,211,153,0.1)"  },
  medium: { label: "Medium", color: "#fbbf24", bg: "rgba(251,191,36,0.1)" },
  high:   { label: "High",   color: "#fb923c", bg: "rgba(249,115,22,0.1)" },
  urgent: { label: "Urgent", color: "#f87171", bg: "rgba(239,68,68,0.1)"  },
};

const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  open:        { label: "Open",        color: "#f87171", bg: "rgba(239,68,68,0.1)"   },
  in_progress: { label: "In Progress", color: "#fbbf24", bg: "rgba(251,191,36,0.1)" },
  resolved:    { label: "Resolved",    color: "#34d399", bg: "rgba(52,211,153,0.1)" },
  closed:      { label: "Closed",      color: "rgba(242,235,217,0.4)", bg: "rgba(100,116,139,0.1)" },
};

interface Ticket {
  id: number;
  title: string;
  description: string | null;
  location: string | null;
  room_id: string | null;
  priority: string;
  status: string;
  assigned_to: string | null;
  reported_by: string | null;
  resolution_notes: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtTime(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

const EMPTY_FORM = { title: "", description: "", location: "", priority: "medium", assigned_to: "" };

export default function MaintenancePage() {
  const [tickets, setTickets]     = useState<Ticket[]>([]);
  const [loading, setLoading]     = useState(true);
  const [toast, setToast]         = useState("");
  const [filterStatus, setFilterStatus]     = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [detailTicket, setDetailTicket] = useState<Ticket | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState("");

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterPriority !== "all") params.priority = filterPriority;
      const { data } = await axios.get(`${API}/api/maintenance`, { params });
      setTickets(data || []);
    } catch {
      showToast("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority]);

  useEffect(() => { load(); }, [load]);

  const createTicket = async () => {
    if (!form.title.trim()) { showToast("Title is required"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/api/maintenance`, form);
      setTickets(prev => [data, ...prev]);
      setForm(EMPTY_FORM);
      setShowForm(false);
      showToast("Ticket created");
    } catch {
      showToast("Failed to create ticket");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (ticket: Ticket, newStatus: string) => {
    try {
      const patch: any = { status: newStatus };
      if (newStatus === "resolved" && resolutionNotes) patch.resolution_notes = resolutionNotes;
      const { data } = await axios.put(`${API}/api/maintenance/${ticket.id}`, patch);
      setTickets(prev => prev.map(t => t.id === ticket.id ? data : t));
      if (detailTicket?.id === ticket.id) setDetailTicket(data);
      showToast(`Ticket ${STATUS_META[newStatus]?.label || newStatus}`);
      setResolutionNotes("");
    } catch {
      showToast("Failed to update ticket");
    }
  };

  const deleteTicket = async (id: number) => {
    if (!confirm("Delete this ticket?")) return;
    try {
      await axios.delete(`${API}/api/maintenance/${id}`);
      setTickets(prev => prev.filter(t => t.id !== id));
      if (detailTicket?.id === id) setDetailTicket(null);
      showToast("Ticket deleted");
    } catch {
      showToast("Failed to delete ticket");
    }
  };

  // Counts by status
  const counts = tickets.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <DashboardLayout>
      <div className="mt-page">

        {/* Header */}
        <div className="mt-header">
          <div>
            <h1 className="mt-title">
              <FontAwesomeIcon icon={faWrench} /> Maintenance
            </h1>
            <p className="mt-subtitle">Track and manage facility maintenance tickets</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="mt-btn-refresh" onClick={load}>
              <FontAwesomeIcon icon={faArrowsRotate} /> Refresh
            </button>
            <button className="mt-btn-new" onClick={() => setShowForm(true)}>
              <FontAwesomeIcon icon={faPlus} /> New Ticket
            </button>
          </div>
        </div>

        {/* Status summary */}
        <div className="mt-status-bar">
          {Object.entries(STATUS_META).map(([key, m]) => (
            <button
              key={key}
              className={`mt-status-pill${filterStatus === key ? " active" : ""}`}
              style={filterStatus === key ? { borderColor: m.color, color: m.color, background: m.bg } : {}}
              onClick={() => setFilterStatus(filterStatus === key ? "all" : key)}
            >
              <span className="mt-pill-count">{counts[key] || 0}</span>
              {m.label}
            </button>
          ))}
          <button
            className={`mt-status-pill${filterStatus === "all" ? " active" : ""}`}
            onClick={() => setFilterStatus("all")}
          >
            <span className="mt-pill-count">{tickets.length}</span>
            All
          </button>
        </div>

        {/* Priority filter */}
        <div className="mt-priority-bar">
          {["all", "urgent", "high", "medium", "low"].map(p => (
            <button
              key={p}
              className={`mt-priority-btn${filterPriority === p ? " active" : ""}`}
              onClick={() => setFilterPriority(p)}
            >
              {p === "all" ? "All Priorities" : PRIORITY_META[p]?.label}
            </button>
          ))}
        </div>

        {/* Ticket list */}
        {loading ? (
          <div className="mt-loading"><FontAwesomeIcon icon={faSpinner} spin /> Loading tickets…</div>
        ) : tickets.length === 0 ? (
          <div className="mt-empty">
            <FontAwesomeIcon icon={faCircleCheck} />
            <p>No maintenance tickets found</p>
          </div>
        ) : (
          <div className="mt-ticket-grid">
            {tickets.map(ticket => {
              const pMeta = PRIORITY_META[ticket.priority] || PRIORITY_META.medium;
              const sMeta = STATUS_META[ticket.status] || STATUS_META.open;
              return (
                <div
                  key={ticket.id}
                  className={`mt-card${ticket.status === "open" && ticket.priority === "urgent" ? " mt-card-urgent" : ""}`}
                  onClick={() => { setDetailTicket(ticket); setResolutionNotes(""); }}
                >
                  <div className="mt-card-top">
                    <span className="mt-card-id">#{ticket.id}</span>
                    <span className="mt-badge" style={{ color: pMeta.color, background: pMeta.bg }}>
                      {pMeta.label}
                    </span>
                    <span className="mt-badge" style={{ color: sMeta.color, background: sMeta.bg }}>
                      {sMeta.label}
                    </span>
                  </div>

                  <h3 className="mt-card-title">{ticket.title}</h3>

                  {ticket.description && (
                    <p className="mt-card-desc">{ticket.description}</p>
                  )}

                  <div className="mt-card-meta">
                    {ticket.location && (
                      <span><FontAwesomeIcon icon={faLocationDot} /> {ticket.location}</span>
                    )}
                    {ticket.assigned_to && (
                      <span><FontAwesomeIcon icon={faUser} /> {ticket.assigned_to}</span>
                    )}
                    <span style={{ marginLeft: "auto", color: "#4a5568" }}>{fmt(ticket.created_at)}</span>
                  </div>

                  <div className="mt-card-actions" onClick={e => e.stopPropagation()}>
                    {ticket.status === "open" && (
                      <button className="mt-action-btn mt-btn-start" onClick={() => updateStatus(ticket, "in_progress")}>
                        Start
                      </button>
                    )}
                    {ticket.status === "in_progress" && (
                      <button className="mt-action-btn mt-btn-resolve" onClick={() => updateStatus(ticket, "resolved")}>
                        <FontAwesomeIcon icon={faCheck} /> Resolve
                      </button>
                    )}
                    {(ticket.status === "resolved" || ticket.status === "in_progress") && (
                      <button className="mt-action-btn mt-btn-close" onClick={() => updateStatus(ticket, "closed")}>
                        Close
                      </button>
                    )}
                    {ticket.status === "closed" && (
                      <button className="mt-action-btn mt-btn-reopen" onClick={() => updateStatus(ticket, "open")}>
                        Reopen
                      </button>
                    )}
                    <button className="mt-action-btn mt-btn-delete" onClick={() => deleteTicket(ticket.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* New Ticket Modal */}
        {showForm && (
          <div className="mt-modal-overlay" onClick={() => setShowForm(false)}>
            <div className="mt-modal" onClick={e => e.stopPropagation()}>
              <div className="mt-modal-header">
                <h3><FontAwesomeIcon icon={faWrench} /> New Maintenance Ticket</h3>
                <button className="mt-modal-close" onClick={() => setShowForm(false)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>

              <div className="mt-form">
                <label>Title *</label>
                <input
                  className="mt-input"
                  placeholder="e.g. Broken AC in room 204"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                />

                <label>Description</label>
                <textarea
                  className="mt-textarea"
                  rows={3}
                  placeholder="Describe the issue in detail…"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                />

                <div className="mt-form-row">
                  <div style={{ flex: 1 }}>
                    <label>Location</label>
                    <input
                      className="mt-input"
                      placeholder="e.g. Room 204, Lobby, Pool"
                      value={form.location}
                      onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label>Priority</label>
                    <select
                      className="mt-select"
                      value={form.priority}
                      onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <label>Assign To</label>
                <input
                  className="mt-input"
                  placeholder="e.g. Ahmed (Maintenance)"
                  value={form.assigned_to}
                  onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
                />
              </div>

              <div className="mt-modal-footer">
                <button className="mt-btn-save" onClick={createTicket} disabled={saving}>
                  {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
                  Create Ticket
                </button>
                <button className="mt-btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Detail/Edit Drawer */}
        {detailTicket && (
          <div className="mt-modal-overlay" onClick={() => setDetailTicket(null)}>
            <div className="mt-modal mt-modal-detail" onClick={e => e.stopPropagation()}>
              <div className="mt-modal-header">
                <h3><FontAwesomeIcon icon={faNoteSticky} /> Ticket #{detailTicket.id}</h3>
                <button className="mt-modal-close" onClick={() => setDetailTicket(null)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>

              <div className="mt-detail-body">
                <div className="mt-detail-badges">
                  {(() => {
                    const p = PRIORITY_META[detailTicket.priority] || PRIORITY_META.medium;
                    const s = STATUS_META[detailTicket.status] || STATUS_META.open;
                    return (
                      <>
                        <span className="mt-badge" style={{ color: p.color, background: p.bg }}>{p.label}</span>
                        <span className="mt-badge" style={{ color: s.color, background: s.bg }}>{s.label}</span>
                      </>
                    );
                  })()}
                </div>

                <h2 className="mt-detail-title">{detailTicket.title}</h2>

                {detailTicket.description && (
                  <p className="mt-detail-desc">{detailTicket.description}</p>
                )}

                <div className="mt-detail-grid">
                  <div className="mt-detail-row">
                    <span className="mt-detail-label">Location</span>
                    <span className="mt-detail-value">{detailTicket.location || "—"}</span>
                  </div>
                  <div className="mt-detail-row">
                    <span className="mt-detail-label">Assigned To</span>
                    <span className="mt-detail-value">{detailTicket.assigned_to || "—"}</span>
                  </div>
                  <div className="mt-detail-row">
                    <span className="mt-detail-label">Reported By</span>
                    <span className="mt-detail-value">{detailTicket.reported_by || "—"}</span>
                  </div>
                  <div className="mt-detail-row">
                    <span className="mt-detail-label">Created</span>
                    <span className="mt-detail-value">{fmtTime(detailTicket.created_at)}</span>
                  </div>
                  {detailTicket.resolved_at && (
                    <div className="mt-detail-row">
                      <span className="mt-detail-label">Resolved</span>
                      <span className="mt-detail-value">{fmtTime(detailTicket.resolved_at)}</span>
                    </div>
                  )}
                </div>

                {detailTicket.resolution_notes && (
                  <div className="mt-detail-notes">
                    <span className="mt-detail-label">Resolution Notes</span>
                    <p>{detailTicket.resolution_notes}</p>
                  </div>
                )}

                {detailTicket.status === "in_progress" && (
                  <div style={{ marginTop: 16 }}>
                    <label className="mt-detail-label">Resolution Notes (optional)</label>
                    <textarea
                      className="mt-textarea"
                      rows={2}
                      placeholder="What was done to fix it?"
                      value={resolutionNotes}
                      onChange={e => setResolutionNotes(e.target.value)}
                    />
                  </div>
                )}

                <div className="mt-detail-actions">
                  {detailTicket.status === "open" && (
                    <button className="mt-action-btn mt-btn-start" onClick={() => updateStatus(detailTicket, "in_progress")}>
                      Start Work
                    </button>
                  )}
                  {detailTicket.status === "in_progress" && (
                    <button className="mt-action-btn mt-btn-resolve" onClick={() => updateStatus(detailTicket, "resolved")}>
                      <FontAwesomeIcon icon={faCheck} /> Mark Resolved
                    </button>
                  )}
                  {(detailTicket.status === "resolved" || detailTicket.status === "in_progress") && (
                    <button className="mt-action-btn mt-btn-close" onClick={() => updateStatus(detailTicket, "closed")}>
                      Close Ticket
                    </button>
                  )}
                  {detailTicket.status === "closed" && (
                    <button className="mt-action-btn mt-btn-reopen" onClick={() => updateStatus(detailTicket, "open")}>
                      Reopen
                    </button>
                  )}
                  <button className="mt-action-btn mt-btn-delete" onClick={() => deleteTicket(detailTicket.id)}>
                    <FontAwesomeIcon icon={faTrash} /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && <div className="mt-toast">{toast}</div>}
      </div>
    </DashboardLayout>
  );
}
